import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendKnowClientMutation } from 'api/tunnelSteps';
import { userSelectors, tunnelActions } from 'reducers';
import { isStatusOk, setTunnelChangesHandlers, validateSchema } from 'utils/helpers';
import { initKYCPM } from 'schemas/tunnel';
import { EURO_SYMBOL, fundTypes } from 'utils/constants';
import { texts } from 'utils/languages/fr';
import defaultTheme from 'config/theme';

import { TunnelStep, TunnelBox } from '../../common';
import { CustomTextField, ErrorMessage, InfoButton, RadioLabel, SubmitButton } from 'components';
import {
    FormControlLabel,
    FormGroup,
    Typography,
    RadioGroup,
    useMediaQuery,
    InputAdornment,
    Box,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const heritageKeys = [
    'heritageAmount',
    'realEstatePercentage',
    'listedSharePercentage',
    'unlistedSharePercentage',
    'opcvmPercentage',
    'availableCashPercentage',
];

const debtKeys = [
    'indebtednessAmount',
    'mainResidencePercentage',
    'rentalInvestmentPercentage',
    'otherCommitmentsPercentage',
    'loanRepaymentPercentage',
    'otherDebtsPercentage',
];

const useStyles = makeStyles({
    form: {
        maxWidth: 660,
        width: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    flexContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
    },
    flexColumn: {
        flexDirection: 'column',
    },
    label: {
        marginRight: 10,
        marginBottom: 0,
        whiteSpace: 'nowrap',
        width: 165,
        overflow: ' hidden',
        textOverflow: 'ellipsis',
    },
    formControlLabel: {
        color: defaultTheme.palette.grey[600],
        margin: '0 0 15px 0',
    },
    amountField: {
        flexShrink: 0,
        marginRight: 10,
        marginBottom: 0,
        [defaultTheme.breakpoints.down('sm')]: {
            width: '100%',
            maxWidth: 300,
        },
    },
    amountLabel: {
        [defaultTheme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'baseline',
            flexDirection: 'column',
        },
    },
    inputField: {
        flexShrink: 0,
        marginRight: 10,
        marginBottom: 0,
        [defaultTheme.breakpoints.down('sm')]: {
            width: 80,
        },
    },
    form4SubQuestion: {
        display: 'block',
        fontSize: 16,
        marginBottom: 20,
        marginTop: 30,
    },
});

const labelSx = { display: 'block', flex: '1 0', mb: 2, color: 'common.black', fontSize: '16px' };

const KYCSurveyPM = props => {
    const { userStep, handleNextStep } = props;

    const classes = useStyles();
    const dispatch = useDispatch();
    const isSmall = useMediaQuery(defaultTheme.breakpoints.down('sm'));

    const [sendKnowClient, { isSuccess: kycSuccess, isLoading: buttonLoader, reset: resetKYC }] =
        useSendKnowClientMutation();

    const { data: messages } = useGetTunnelMessagesQuery();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFCPR = tunnelSubscription?.investmentFund?.type === fundTypes.FCPR;

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const defaultForm = useMemo(
        () => initKYCPM(tunnelSubscription?.kycSurvey),
        [tunnelSubscription?.kycSurvey]
    );
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        totalBalanceLevel: '',
        totalTurnoverLevel: '',
        totalEquityLevel: '',
        heritage: { amount: '', percentTotal: '' },
        assetInvestor: '',
        assetPercentage: '',
        debt: { amount: '', percentTotal: '' },
    });

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const investmentSum = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.investingSum,
        [tunnelSubscription]
    );

    const investorQualification = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.investorQualification,
        [tunnelSubscription]
    );

    const fixedCriteria = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.fixedCriteria || '',
        [tunnelSubscription]
    );

    // keep the form values in sync with the DB
    useEffect(() => {
        setForm(defaultForm);
    }, [defaultForm]);

    const schema = useMemo(
        () => ({
            totalBalanceLevel: Joi.string().required().messages({
                'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
            }),
            totalTurnoverLevel: Joi.string().required().messages({
                'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
            }),
            totalEquityLevel: Joi.string().required().messages({
                'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
            }),
            assetInvestor: isSLP
                ? Joi.any()
                : Joi.string().valid('Oui', 'Non').messages({
                      'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                      'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                  }),
            assetPercentage: Joi.any()
                .when('assetInvestor', { is: 'Oui', then: Joi.string().required() })
                .messages({
                    'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                    'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                }),
        }),
        [isSLP]
    );

    const totalHeritagePercent = useMemo(
        () => heritageKeys.slice(1).reduce((acc, crt) => acc + (+form[crt] || 0), 0),
        [form]
    );
    const totalDebtPercent = useMemo(
        () => debtKeys.slice(1).reduce((acc, crt) => acc + (+form[crt] || 0), 0),
        [form]
    );

    // ---- Validations ----
    const validateHeritage = useCallback(() => {
        const allInputsEmpty = heritageKeys.slice(1).every(key => !form[key]);
        let amountError = '';

        if (!form.heritageAmount) {
            amountError = texts.SAISIR_LE_CHAMP;
        }
        if (allInputsEmpty) {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: texts.SAISIR_LES_POURCENTAGES },
            }));
        } else if (totalHeritagePercent !== 100 && totalHeritagePercent > 0) {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: texts.TOTAL_DES_POURCENTAGES },
            }));
        } else {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: '' },
            }));
        }

        return totalHeritagePercent === 100 && form.heritageAmount;
    }, [totalHeritagePercent, form]);

    const validateDebt = useCallback(() => {
        const allInputsEmpty = debtKeys.slice(1).every(key => !form[key]);

        let percentTotalError = '';

        if (allInputsEmpty || totalDebtPercent === 0) {
            percentTotalError = +form.indebtednessAmount ? texts.SAISIR_LES_POURCENTAGES : '';
        } else if (totalDebtPercent !== 100 && totalDebtPercent > 0) {
            percentTotalError = texts.TOTAL_DES_POURCENTAGES;
        } else if (+form.indebtednessAmount && totalDebtPercent !== 100) {
            percentTotalError = texts.TOTAL_DES_POURCENTAGES;
        }

        setErrors(prevState => ({
            ...prevState,
            debt: {
                amount: +form.indebtednessAmount ? '' : texts.SAISIR_LE_CHAMP,
                percentTotal: percentTotalError,
            },
        }));

        return (
            (totalDebtPercent === 100 && form.indebtednessAmount > 0) ||
            (+form.indebtednessAmount === 0 && totalDebtPercent === 0)
        );
    }, [totalDebtPercent, form]);

    const validateForm = useCallback(() => {
        if (tunnelSubscription.indirectUntransferred) {
            return (
                (!totalDebtPercent || totalDebtPercent === 100) &&
                (!totalHeritagePercent || totalHeritagePercent === 100)
            );
        } else {
            let { valid: schemaValid, newErrors } = validateSchema(schema, form);

            let heritageValid = validateHeritage();
            let debtValid = validateDebt();
            Object.keys(schema).forEach(key => {
                // fill the errors object with empty string so we would overwrite previous errors
                if (!newErrors[key]) newErrors[key] = '';
            });
            setErrors(prevState => ({ ...prevState, ...newErrors }));

            return schemaValid && heritageValid && debtValid;
        }
    }, [
        tunnelSubscription.indirectUntransferred,
        totalDebtPercent,
        totalHeritagePercent,
        schema,
        form,
        validateHeritage,
        validateDebt,
    ]);

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => !isEqual(defaultForm, form), [defaultForm, form]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (validateForm()) {
                resetKYC();
                sendKnowClient({
                    callback: afterSubmitCallback,
                    ...form,
                });
            }
        },
        [form, validateForm, sendKnowClient, resetKYC]
    );

    const hasWarningType1 = useMemo(() => {
        if (investorQualification === 'step34_fpci_pm_real.question_16.label') {
            if (
                fixedCriteria.includes('step34_fpci_pm_real.question_16.option_1') &&
                (form.totalBalanceLevel === 'step5.question_total_balance_level.option_2' ||
                    form.totalBalanceLevel === 'step5.question_total_balance_level.option_1')
            ) {
                return true;
            }

            if (
                fixedCriteria.includes('step34_fpci_pm_real.question_16.option_2') &&
                (form.totalTurnoverLevel === 'step5.question_total_turnover_level.option_1' ||
                    form.totalTurnoverLevel === 'step5.question_total_turnover_level.option_2' ||
                    form.totalTurnoverLevel === 'step5.question_total_turnover_level.option_3')
            ) {
                return true;
            }

            if (
                fixedCriteria.includes('step34_fpci_pm_real.question_16.option_3') &&
                (form.totalEquityLevel === 'step5.question_total_equity_level.option_1' ||
                    form.totalEquityLevel === 'step5.question_total_equity_level.option_2')
            ) {
                return true;
            }
        }
        return false;
    }, [investorQualification, form, fixedCriteria]);

    const hasWarningType2 = useMemo(
        () => investmentSum >= form.heritageAmount / 2,
        [investmentSum, form]
    );

    const startSubmit = useCallback(
        afterSubmitCallback => () => {
            if (
                (hasWarningType1 || hasWarningType2) &&
                !tunnelSubscription.indirectUntransferred &&
                !isSLP &&
                !isFCPR
            ) {
                // skip this part for indirect subscriptions
                dispatch(
                    tunnelActions.setIneligible({
                        open: true,
                        message: '',
                        subMessage: hasWarningType1
                            ? texts.INCOHERENT_ANSWER
                            : texts.PROFIL_INVESTISSEUR_INCORRECT,
                        canConfirm: true,
                        confirmMessage: texts.CONFIRM_INELLIGIBLE,
                        onConfirm: handleSubmit(),
                    })
                );
            } else if (!changesMade && tunnelSubscription.indirectUntransferred) {
                handleNextStep(true);
            } else {
                handleSubmit(afterSubmitCallback)();
            }
        },
        [
            hasWarningType1,
            hasWarningType2,
            changesMade,
            handleSubmit,
            dispatch,
            tunnelSubscription,
            handleNextStep,
            isSLP,
            isFCPR,
        ]
    );

    useEffect(() => {
        if (kycSuccess) {
            handleNextStep();
        }
    }, [kycSuccess, handleNextStep]);

    useEffect(
        () => setTunnelChangesHandlers(startSubmit, checkChanges, isResubmit),
        [startSubmit, checkChanges, isResubmit]
    );

    return (
        <TunnelStep
            title={texts.INVESTOR_SURVEY_PM}
            subtitle={isSLP ? texts.KYC_SURVEY_SLP_SUBTITLE : texts.INVESTOR_SURVEY_PM_SUBTITLE}
        >
            <TunnelBox>
                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_total_balance_level.label']}
                        </Typography>
                    </Box>
                    <RadioGroup
                        onChange={handleChange('totalBalanceLevel')}
                        value={form.totalBalanceLevel}
                    >
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <RadioLabel
                                label={messages[`step5.question_total_balance_level.option_${i}`]}
                                key={`step5.question_total_balance_level.option_${i}`}
                                value={`step5.question_total_balance_level.option_${i}`}
                            />
                        ))}
                    </RadioGroup>
                    <ErrorMessage message={errors.totalBalanceLevel} />
                </Box>
                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_total_turnover_level.label']}
                        </Typography>
                    </Box>
                    <RadioGroup
                        onChange={handleChange('totalTurnoverLevel')}
                        value={form.totalTurnoverLevel}
                    >
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <RadioLabel
                                label={messages[`step5.question_total_turnover_level.option_${i}`]}
                                key={`step5.question_total_turnover_level.option_${i}`}
                                value={`step5.question_total_turnover_level.option_${i}`}
                            />
                        ))}
                    </RadioGroup>
                    <ErrorMessage message={errors.totalTurnoverLevel} />
                </Box>
                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_total_equity_level.label']}
                        </Typography>
                    </Box>
                    <RadioGroup
                        onChange={handleChange('totalEquityLevel')}
                        value={form.totalEquityLevel}
                    >
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <RadioLabel
                                label={messages[`step5.question_total_equity_level.option_${i}`]}
                                key={`step5.question_total_equity_level.option_${i}`}
                                value={`step5.question_total_equity_level.option_${i}`}
                            />
                        ))}
                    </RadioGroup>
                    <ErrorMessage message={errors.totalEquityLevel} />
                </Box>
                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_3.label_pm']}
                        </Typography>
                        <InfoButton
                            disableMarginBottom
                            title={texts.PATRIMOINE_GLOBAL}
                            content={
                                <React.Fragment>
                                    <Typography gutterBottom>
                                        {texts.PATRIMOINE_INFO_1_PM}
                                    </Typography>
                                    <Typography gutterBottom>{texts.PATRIMOINE_INFO_2}</Typography>
                                </React.Fragment>
                            }
                        />
                    </Box>
                    <FormGroup className={clsx(classes.flexContainer, classes.flexColumn)}>
                        {heritageKeys.map((heritageType, i) => (
                            <FormControlLabel
                                key={heritageType}
                                className={clsx(classes.formControlLabel, {
                                    [classes.amountLabel]: i === 0,
                                })}
                                control={
                                    <CustomTextField
                                        fullWidth={isSmall && i === 0}
                                        value={form[heritageType]}
                                        inputStyle={
                                            i === 0 ? classes.amountField : classes.inputField
                                        }
                                        onChange={handleChange(heritageType)}
                                        error={i === 0 ? errors.heritage?.amount : ''}
                                        showError={false}
                                        type={i === 0 ? undefined : 'nubmer'}
                                        formatNumber={i === 0}
                                        endAdornment={
                                            <InputAdornment position="start">
                                                {i === 0 ? EURO_SYMBOL : '%'}
                                            </InputAdornment>
                                        }
                                    />
                                }
                                label={messages[`step5.question_3.option_${i + 1}`]}
                            />
                        ))}
                    </FormGroup>
                    <ErrorMessage message={errors.heritage?.percentTotal} />
                </Box>
                {!isSLP && (
                    <Box mb={3}>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between">
                            <Typography sx={labelSx}>
                                {messages['step5.question_asset_investor.label']}
                            </Typography>
                            <InfoButton
                                disableMarginBottom
                                content={
                                    <React.Fragment>
                                        <Typography gutterBottom>
                                            {texts.INVESTISSMENTS_NON_COTE}
                                        </Typography>
                                        <Typography gutterBottom>
                                            {texts.PAS_DE_FONDS_DE_CAPITAL}
                                        </Typography>
                                    </React.Fragment>
                                }
                            />
                        </Box>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleChange('assetInvestor')}
                            value={form.assetInvestor}
                        >
                            <RadioLabel
                                label={messages['general_options.yes']}
                                value={messages['general_options.yes']}
                            />
                            <RadioLabel
                                label={messages['general_options.no']}
                                value={messages['general_options.no']}
                            />
                        </RadioGroup>
                        {form.assetInvestor === 'Oui' && (
                            <>
                                <label className={classes.form4SubQuestion}>
                                    {messages['step5.question_5.label']}
                                </label>
                                <RadioGroup
                                    className={classes.flexContainer}
                                    onChange={handleChange('assetPercentage')}
                                    value={form.assetPercentage}
                                >
                                    {[1, 2, 3].map(i => (
                                        <RadioLabel
                                            key={`step5.question_5.option_${i}`}
                                            value={`step5.question_5.option_${i}`}
                                            label={messages[`step5.question_5.option_${i}`]}
                                        />
                                    ))}
                                </RadioGroup>
                            </>
                        )}
                        <ErrorMessage message={errors.assetInvestor} />
                    </Box>
                )}
                <Box>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_4.label_fcpi_pm_real']}
                        </Typography>
                    </Box>
                    <FormGroup className={clsx(classes.flexContainer, classes.flexColumn)}>
                        {debtKeys.map((debtType, i) => (
                            <FormControlLabel
                                key={debtType}
                                className={clsx(classes.formControlLabel, {
                                    [classes.amountLabel]: i === 0,
                                })}
                                control={
                                    <CustomTextField
                                        value={form[debtType]}
                                        inputStyle={
                                            i === 0 ? classes.amountField : classes.inputField
                                        }
                                        onChange={handleChange(debtType)}
                                        type={i === 0 ? undefined : 'nubmer'}
                                        formatNumber={i === 0}
                                        endAdornment={
                                            <InputAdornment position="start">
                                                {i === 0 ? EURO_SYMBOL : '%'}
                                            </InputAdornment>
                                        }
                                    />
                                }
                                label={messages[`step5.question_4.option_${i + 1}_fcpi_pm_real`]}
                            />
                        ))}
                    </FormGroup>
                    <ErrorMessage message={errors.debt?.percentTotal} />
                </Box>
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                disabled={buttonLoader}
                loading={buttonLoader}
                onClick={changesMade || !hasStatusOk ? startSubmit() : handleNextStep}
            />
        </TunnelStep>
    );
};

KYCSurveyPM.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default KYCSurveyPM;
